import React, { useState } from 'react';
import { Mic, MicOff, Video, VideoOff, Lock, ArrowRight, Loader2, Settings, User as UserIcon } from 'lucide-react';
import { DeviceInfo } from '../hooks/useMediaDevices';
import { DeviceSelector } from './DeviceSelector';

interface LobbyProps {
  title: string;
  hasPassword: boolean;
  isHost?: boolean;
  isAuthenticated?: boolean;
  initialGuestName?: string;
  localStream: MediaStream | null;
  isAudioMuted: boolean;
  isVideoOff: boolean;
  audioDevices: DeviceInfo[];
  videoDevices: DeviceInfo[];
  selectedAudioId: string;
  selectedVideoId: string;
  onSelectAudio: (id: string) => void;
  onSelectVideo: (id: string) => void;
  onToggleAudio: () => void;
  onToggleVideo: () => void;
  onJoin: (password?: string, name?: string) => Promise<void>;
}

export const Lobby: React.FC<LobbyProps> = ({
  title,
  hasPassword,
  isHost = false,
  isAuthenticated = false,
  initialGuestName = '',
  localStream,
  isAudioMuted,
  isVideoOff,
  audioDevices,
  videoDevices,
  selectedAudioId,
  selectedVideoId,
  onSelectAudio,
  onSelectVideo,
  onToggleAudio,
  onToggleVideo,
  onJoin,
}) => {
  const [guestName, setGuestName] = useState(initialGuestName);
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showSettings, setShowSettings] = useState(false);

  const handleJoin = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    const trimmedName = guestName.trim();
    if (!isAuthenticated && !trimmedName) {
      setError('Please enter your name to join the meeting');
      return;
    }

    if (hasPassword && !isHost && !password.trim()) {
      setError('Meeting password is required');
      return;
    }

    setLoading(true);
    try {
      await onJoin(
        password.trim() ? password.trim() : undefined,
        !isAuthenticated ? trimmedName : undefined
      );
    } catch (err: any) {
      setError(err.message || 'Failed to join meeting');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#090b10] flex items-center justify-center p-6">
      <div className="w-full max-w-4xl grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
        {/* Left Column: Video Preview */}
        <div className="lg:col-span-7 flex flex-col items-center">
          <div className="relative w-full aspect-video rounded-3xl overflow-hidden bg-[#121622] border border-slate-800 shadow-2xl flex items-center justify-center">
            {!isVideoOff && localStream ? (
              <video
                ref={(el) => {
                  if (el && localStream) el.srcObject = localStream;
                }}
                autoPlay
                playsInline
                muted
                className="w-full h-full object-cover scale-x-[-1]"
              />
            ) : (
              <div className="flex flex-col items-center space-y-2 text-slate-500">
                <div className="w-16 h-16 rounded-full bg-slate-800 flex items-center justify-center text-slate-400">
                  <VideoOff className="w-8 h-8" />
                </div>
                <span className="text-xs font-medium">Camera is off</span>
              </div>
            )}

            {/* Quick Preview Controls Bar */}
            <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex items-center space-x-3 bg-[#090b10]/80 backdrop-blur-md px-4 py-2 rounded-2xl border border-slate-700/50 shadow-lg">
              <button
                type="button"
                onClick={onToggleAudio}
                title={isAudioMuted ? 'Unmute' : 'Mute'}
                className={`p-3 rounded-xl transition ${
                  isAudioMuted
                    ? 'bg-rose-500 text-white'
                    : 'bg-slate-800 hover:bg-slate-700 text-slate-200'
                }`}
              >
                {isAudioMuted ? <MicOff className="w-4 h-4" /> : <Mic className="w-4 h-4" />}
              </button>

              <button
                type="button"
                onClick={onToggleVideo}
                title={isVideoOff ? 'Start Video' : 'Stop Video'}
                className={`p-3 rounded-xl transition ${
                  isVideoOff
                    ? 'bg-rose-500 text-white'
                    : 'bg-slate-800 hover:bg-slate-700 text-slate-200'
                }`}
              >
                {isVideoOff ? <VideoOff className="w-4 h-4" /> : <Video className="w-4 h-4" />}
              </button>

              <button
                type="button"
                onClick={() => setShowSettings(true)}
                title="Settings"
                className="p-3 rounded-xl bg-slate-800 hover:bg-slate-700 text-slate-200 transition"
              >
                <Settings className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>

        {/* Right Column: Meeting Info & Join Action */}
        <div className="lg:col-span-5 space-y-6">
          <div className="space-y-2">
            <span className="text-xs font-semibold uppercase tracking-wider text-blue-400 bg-blue-500/10 px-2.5 py-1 rounded-full border border-blue-500/20">
              Ready to Join
            </span>
            <h1 className="text-2xl font-bold text-white tracking-tight">{title}</h1>
            <p className="text-xs text-slate-400">
              Check your audio and video before entering the room.
            </p>
          </div>

          <form onSubmit={handleJoin} className="space-y-4">
            {!isAuthenticated && (
              <div className="space-y-1.5">
                <label className="text-xs font-semibold text-slate-300 flex items-center space-x-1.5">
                  <UserIcon className="w-3.5 h-3.5 text-blue-400" />
                  <span>Your Name <span className="text-rose-400">*</span></span>
                </label>
                <input
                  type="text"
                  value={guestName}
                  onChange={(e) => setGuestName(e.target.value)}
                  placeholder="Enter your name to join"
                  required
                  maxLength={60}
                  className="w-full px-4 py-3 rounded-xl bg-slate-900 border border-slate-800 text-sm text-white placeholder-slate-500 focus:outline-none focus:border-blue-500 transition shadow-inner"
                />
              </div>
            )}

            {hasPassword && !isHost && (
              <div className="space-y-1.5">
                <label className="text-xs font-semibold text-slate-300 flex items-center space-x-1.5">
                  <Lock className="w-3.5 h-3.5 text-blue-400" />
                  <span>Meeting Password <span className="text-rose-400">*</span></span>
                </label>
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter meeting password"
                  required
                  className="w-full px-4 py-3 rounded-xl bg-slate-900 border border-slate-800 text-sm text-white placeholder-slate-500 focus:outline-none focus:border-blue-500 transition shadow-inner"
                />
              </div>
            )}

            {isHost && (
              <div className="p-2.5 rounded-xl bg-blue-500/10 border border-blue-500/20 text-blue-300 text-xs flex items-center space-x-2">
                <span className="w-2 h-2 rounded-full bg-blue-400"></span>
                <span>You are entering as the meeting host</span>
              </div>
            )}

            {error && (
              <div className="p-3 rounded-xl bg-rose-500/10 border border-rose-500/20 text-rose-400 text-xs">
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              className="w-full py-3.5 px-6 rounded-2xl bg-blue-600 hover:bg-blue-500 text-white font-semibold text-sm shadow-lg shadow-blue-600/25 flex items-center justify-center space-x-2 transition disabled:opacity-50"
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  <span>Connecting...</span>
                </>
              ) : (
                <>
                  <span>Join Meeting</span>
                  <ArrowRight className="w-4 h-4" />
                </>
              )}
            </button>
          </form>
        </div>
      </div>

      {showSettings && (
        <DeviceSelector
          audioDevices={audioDevices}
          videoDevices={videoDevices}
          selectedAudioId={selectedAudioId}
          selectedVideoId={selectedVideoId}
          onSelectAudio={onSelectAudio}
          onSelectVideo={onSelectVideo}
          onClose={() => setShowSettings(false)}
        />
      )}
    </div>
  );
};
